import getConfig from './config';
import { indexerFetch } from './indexerFallback';
import { wallet } from './near';
import { getTotalPools } from './pool';

let cached: { at: number; empty: boolean } | null = null;

const asCount = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  const count = Number(value);
  return Number.isFinite(count) ? count : null;
};

const accountMissing = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error);
  return /does not exist|MethodNotFound|method .+ not found/i.test(message);
};

// A missing concentrated-liquidity contract is the same as having no CLMM pools.
const clmmPoolCount = async (): Promise<number | null> => {
  const univ4 = getConfig().UNIV4_CONTRACT_ID;
  if (!univ4) return 0;
  try {
    const pools = await wallet.account().viewFunction(univ4, 'get_pools', {
      from_index: 0,
      limit: 1,
    });
    return Array.isArray(pools) ? pools.length : null;
  } catch (error) {
    return accountMissing(error) ? 0 : null;
  }
};

// Same classic-pool search the pools page uses. An empty indexer is the
// "zero classic pools" the swap page has to explain.
const indexerClassicCount = async (): Promise<number | null> => {
  try {
    const response = await indexerFetch(
      '/pool/search?type=classic&sort=tvl&limit=1&offset=0&farm=false&hide_low_pool=false&order_by=desc',
      { method: 'GET' }
    );
    const body = await response.json();
    if (body?.data && body.data.total !== undefined) {
      return asCount(body.data.total);
    }
    if (Array.isArray(body?.data?.list)) return body.data.list.length;
    if (Array.isArray(body)) return body.length;
    return null;
  } catch (error) {
    return null;
  }
};

/**
 * True when every source we could read reports zero pools.
 * A failed read is ignored, so an empty indexer still explains the swap
 * when the contract view is not ready. If nothing could be read, this
 * stays false and the pair-specific error remains.
 */
export async function exchangeHasNoPools(): Promise<boolean> {
  if (cached && Date.now() - cached.at < 20000) return cached.empty;

  const [classic, clmm, indexed] = await Promise.all([
    getTotalPools()
      .then(asCount)
      .catch(() => null),
    clmmPoolCount(),
    indexerClassicCount(),
  ]);

  const known = [classic, clmm, indexed].filter(
    (count): count is number => count !== null
  );
  const empty = known.length > 0 && known.every((count) => count === 0);
  cached = { at: Date.now(), empty };
  return empty;
}
